import React, { useState } from 'react';
import { PhoneIcon, MailIcon, MapPinIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { NordenMascot } from './NordenMascot';
const MAIL_ENDPOINT = 'https://scan.noorddigital.nl/mail.php';
export function FinalCtaSection() {
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    telefoon: '',
    bericht: ''
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.naam.trim() || !formData.email.trim() || !formData.bericht.trim()) {
      setErrorMsg('Vul alle verplichte velden in.');
      return;
    }
    setErrorMsg('');
    setStatus('sending');
    try {
      const body = new URLSearchParams({
        naam: formData.naam.trim(),
        email: formData.email.trim(),
        telefoon: formData.telefoon.trim(),
        bericht: formData.bericht.trim(),
        ontvanger: 'info@noorddigital.nl',
        onderwerp: `Nieuw contactformulier Webdesign Alkmaar — ${formData.naam.trim()}`
      });
      const res = await fetch(MAIL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMsg('Versturen mislukt. Probeer opnieuw of mail info@noorddigital.nl.');
    }
  };
  return <section id="contact" className="relative py-24 lg:py-32 bg-white overflow-hidden" data-id="element-377">
      {/* Norden with plant */}
      <div className="absolute top-12 right-4 lg:right-12 opacity-[0.07] pointer-events-none hidden lg:block" data-id="element-378">
        <NordenMascot variant="plant" size="xl" data-id="element-379" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-380">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start" data-id="element-381">
          {/* Left column */}
          <div data-id="element-382">
            <p className="text-accent font-semibold text-sm tracking-widest uppercase mb-3" data-id="element-383">Contact</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-6" data-id="element-384">Neem contact op</h2>
            <p className="text-lg text-navy-600 leading-relaxed mb-10" data-id="element-385">
              Heb je een vraag over onze diensten of wil je vrijblijvend sparren over jouw website? Vul het formulier in en we nemen binnen 24 uur contact met je op.
            </p>

            <div className="space-y-4 mb-8" data-id="element-386">
              <a href="tel:+31616265214" className="flex items-center gap-4 text-navy-700 hover:text-navy-900 transition-colors" data-id="element-387">
                <div className="w-10 h-10 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-388">
                  <PhoneIcon className="w-5 h-5 text-accent" data-id="element-389" />
                </div>
                <span className="font-medium" data-id="element-390">+31 6 16265214</span>
              </a>

              <a href="mailto:info@noorddigital.nl" className="flex items-center gap-4 text-navy-700 hover:text-navy-900 transition-colors" data-id="element-391">
                <div className="w-10 h-10 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-392">
                  <MailIcon className="w-5 h-5 text-accent" data-id="element-393" />
                </div>
                <span className="font-medium" data-id="element-394">info@noorddigital.nl</span>
              </a>

              <div className="flex items-center gap-4 text-navy-700" data-id="element-395">
                <div className="w-10 h-10 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-396">
                  <MapPinIcon className="w-5 h-5 text-accent" data-id="element-397" />
                </div>
                <span className="font-medium" data-id="element-398">Schuit 8a, 1724 BD Oudkarspel</span>
              </div>
            </div>

            <a href="https://scan.noorddigital.nl" className="inline-flex items-center gap-2 text-accent hover:text-accent-hover font-semibold transition-colors group" data-id="element-399">
              Of doe direct de gratis websitescan
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-400" />
            </a>

            <div className="mt-10 hidden lg:flex items-center gap-4" data-id="element-401">
              <NordenMascot variant="lightbulb" size="md" data-id="element-402" />
              <p className="text-sm text-navy-500 italic" data-id="element-403">Norden staat klaar om je te helpen!</p>
            </div>
          </div>

          {/* Right column */}
          <div className="bg-white rounded-2xl shadow-card p-8 lg:p-10" data-id="element-404">
            {status === 'success' ? <div className="text-center py-12" data-id="element-405">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6" data-id="element-406">
                  <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-407" />
                </div>
                <h3 className="text-2xl font-bold text-navy-900 mb-3" data-id="element-408">Bedankt!</h3>
                <p className="text-navy-600" data-id="element-409">We nemen snel contact met je op.</p>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-410">
                <div data-id="element-411">
                  <label htmlFor="naam" className="block text-sm font-medium text-navy-700 mb-1.5" data-id="element-412">Naam *</label>
                  <input type="text" id="naam" name="naam" required value={formData.naam} onChange={handleChange} placeholder="Jouw naam" className="w-full bg-white border border-navy-200 rounded-xl px-4 py-3 text-navy-900 placeholder-navy-400 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all" data-id="element-413" />
                </div>

                <div data-id="element-414">
                  <label htmlFor="email" className="block text-sm font-medium text-navy-700 mb-1.5" data-id="element-415">E-mail *</label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} placeholder="jouw@email.nl" className="w-full bg-white border border-navy-200 rounded-xl px-4 py-3 text-navy-900 placeholder-navy-400 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all" data-id="element-416" />
                </div>

                <div data-id="element-417">
                  <label htmlFor="telefoon" className="block text-sm font-medium text-navy-700 mb-1.5" data-id="element-418">Telefoon</label>
                  <input type="tel" id="telefoon" name="telefoon" value={formData.telefoon} onChange={handleChange} placeholder="06 12345678" className="w-full bg-white border border-navy-200 rounded-xl px-4 py-3 text-navy-900 placeholder-navy-400 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all" data-id="element-419" />
                </div>

                <div data-id="element-420">
                  <label htmlFor="bericht" className="block text-sm font-medium text-navy-700 mb-1.5" data-id="element-421">Bericht *</label>
                  <textarea id="bericht" name="bericht" required rows={4} value={formData.bericht} onChange={handleChange} placeholder="Waar kunnen we je mee helpen?" className="w-full bg-white border border-navy-200 rounded-xl px-4 py-3 text-navy-900 placeholder-navy-400 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all resize-none" data-id="element-422" />
                </div>

                {errorMsg && <p className="text-red-500 text-sm" data-id="element-423">{errorMsg}</p>}

                <button type="submit" disabled={status === 'sending'} className="w-full bg-accent hover:bg-accent-hover disabled:opacity-60 text-white font-semibold py-4 rounded-button shadow-soft hover:shadow-medium transition-all duration-200 text-base" data-id="element-424">
                  {status === 'sending' ? 'Versturen...' : 'Verstuur bericht'}
                </button>

                <p className="text-sm text-navy-500 text-center" data-id="element-425">We reageren binnen 24 uur.</p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}